/* ── Convert Shot Flash ───────────────────────────────────────────
 * Quick crosshair-style flash at the convert target: two crossed
 * rectangles plus a thin ring read as "lock-on / converted!". Short
 * lifetime (~0.5s) so it punches in then disappears.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec2, vec3, vec4, float, length, smoothstep } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 0.5;
const CROSS_LENGTH = 4;
const CROSS_WIDTH = 0.35;
const RING_RADIUS = 2.5;
const COLOR_HEX = 0xff6cbb;

interface ConvertShotFlashOptions {
  liveResolver?: LivePositionResolver;
}

export class ConvertShotFlash extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly group: THREE.Group;
  private readonly crossGeometry: THREE.PlaneGeometry;
  private readonly ringGeometry: THREE.RingGeometry;
  private readonly crossMaterial: THREE.MeshBasicNodeMaterial;
  private readonly ringMaterial: THREE.MeshBasicNodeMaterial;
  private readonly uProgress: ReturnType<typeof uniform>;
  private readonly liveResolver?: LivePositionResolver;

  constructor(scene: THREE.Scene, position: Vec3Like, options?: ConvertShotFlashOptions) {
    super(scene, LIFE_SECONDS);
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y, position.z);
    this.liveResolver = options?.liveResolver;

    this.uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const uProgress = this.uProgress;

    this.crossGeometry = new THREE.PlaneGeometry(CROSS_LENGTH, CROSS_WIDTH);
    this.crossMaterial = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const crossColor = Fn(() => {
      const u = uv();
      const alongFade = smoothstep(float(0.0), float(0.15), u.x).mul(
        smoothstep(float(1.0), float(0.85), u.x),
      );
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(0.4, 0.2, 0.4).mul(alongFade));
      const alpha = alongFade.mul(fade);
      return vec4(rgb, alpha);
    })();
    this.crossMaterial.colorNode = crossColor;
    setEmissiveNode(this.crossMaterial, crossColor);

    const crossH = new THREE.Mesh(this.crossGeometry, this.crossMaterial);
    const crossV = new THREE.Mesh(this.crossGeometry, this.crossMaterial);
    crossV.rotation.z = Math.PI / 2;
    this.group.add(crossH);
    this.group.add(crossV);

    this.ringGeometry = new THREE.RingGeometry(RING_RADIUS * 0.9, RING_RADIUS, 48, 1);
    this.ringMaterial = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const ringColor = Fn(() => {
      const u = uv();
      const radial = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
      const edge = smoothstep(float(0.85), float(1.0), radial);
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(0.3, 0.1, 0.3).mul(edge));
      const alpha = edge.mul(fade);
      return vec4(rgb, alpha);
    })();
    this.ringMaterial.colorNode = ringColor;
    setEmissiveNode(this.ringMaterial, ringColor);
    this.group.add(new THREE.Mesh(this.ringGeometry, this.ringMaterial));

    this.scene.add(this.group);
  }

  protected tick(t: number): void {
    this.uProgress.value = t;
    this.applyLiveAnchor(this.group, this.liveResolver);
    // Ease-in pop then linger: scale grows fast then plateaus.
    this.group.scale.setScalar(Math.min(1, t * 3));
  }

  protected disposeResources(): void {
    this.scene.remove(this.group);
    this.crossGeometry.dispose();
    this.ringGeometry.dispose();
    this.crossMaterial.dispose();
    this.ringMaterial.dispose();
  }
}
